import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "../api";
import { Alert, Badge, Card, DataTable, Empty, ErrorBox, Loading, Money, PageHeader, Tabs, TextInput } from "../ui";
import { DeptBadge, date } from "../format";
import { formatGBP } from "../../shared/money";
import { londonDate, monthStart } from "../../shared/dates";
import { PAYMENT_METHOD_LABEL } from "../../shared/labels";

const TABS = [
  { value: "pnl", label: "Profit & loss" }, { value: "cash", label: "Cash movements" }, { value: "sales", label: "Sales vs received" },
  { value: "debtors", label: "Owed to us" }, { value: "creditors", label: "We owe" }, { value: "expenses", label: "Expenses" },
  { value: "jobs", label: "Job profit" }, { value: "departments", label: "Mechanical vs bodywork" }, { value: "trial-balance", label: "Trial balance" }, { value: "exports", label: "Accountant exports" },
] as const;
type Tab = (typeof TABS)[number]["value"];

export default function Reports() {
  const { tab = "pnl" } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const today = londonDate();
  const from = params.get("from") ?? monthStart(today);
  const to = params.get("to") ?? today;
  const range = { from, to };
  return (
    <>
      <PageHeader title="Reports" sub="Management reports from the ledger. They are not statutory accounts or a tax return." />
      <Tabs value={tab as Tab} onChange={(t) => navigate(`/reports/${t}${qs(range)}`)} tabs={TABS as any} />
      {!["debtors", "creditors"].includes(tab) && (
        <div className="filters">
          <TextInput label={tab === "trial-balance" ? "As at" : "From"} type="date" value={tab === "trial-balance" ? to : from} onChange={(e) => setParams(tab === "trial-balance" ? { from, to: e.target.value } : { from: e.target.value, to }, { replace: true })} />
          {tab !== "trial-balance" && <TextInput label="To" type="date" value={to} onChange={(e) => setParams({ from, to: e.target.value }, { replace: true })} />}
        </div>
      )}
      {tab === "pnl" && <Pnl {...range} />}
      {tab === "cash" && <Cash {...range} />}
      {tab === "sales" && <Sales {...range} />}
      {tab === "debtors" && <Debtors />}
      {tab === "creditors" && <Creditors />}
      {tab === "expenses" && <Expenses {...range} />}
      {tab === "jobs" && <Jobs {...range} />}
      {tab === "departments" && <Departments {...range} />}
      {tab === "trial-balance" && <TrialBalance asOf={to} />}
      {tab === "exports" && <Exports {...range} />}
    </>
  );
}

function useReport(path: string, params: Record<string, string> = {}) {
  return useQuery({ queryKey: ["report", path, params], queryFn: () => api.get(`/reports/${path}${qs(params)}`) });
}
function Wrap({ q, children }: { q: any; children: (d: any) => React.ReactNode }) {
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  return <>{children(q.data)}</>;
}
const Basis = ({ text }: { text: string }) => <p className="small muted" style={{ marginTop: 0 }}><strong>Basis:</strong> {text}</p>;

function Section({ title, rows, total }: { title: string; rows: any[]; total: number }) {
  if (!rows.length) return null;
  return <>
    <tr><td colSpan={2} style={{ fontWeight: 700, background: "var(--grey-50)" }}>{title}</td></tr>
    {rows.map((r) => <tr key={r.id}><td style={{ paddingLeft: 24 }}>{r.name} <span className="small muted">{r.code}</span></td><td className="num">{formatGBP(r.amount)}</td></tr>)}
    <tr><td className="right small muted">Total {title.toLowerCase()}</td><td className="num"><strong>{formatGBP(total)}</strong></td></tr>
  </>;
}

function Pnl(r: { from: string; to: string }) {
  const q = useReport("pnl", r);
  return <Wrap q={q}>{(d) => (
    <Card title={`Profit & loss ${date(d.from)} – ${date(d.to)}`}>
      <Basis text={d.basis} />
      {d.warnings.map((w: string) => <div key={w} style={{ marginBottom: 8 }}><Alert kind="warn" title="Incomplete costs">{w}</Alert></div>)}
      <div className="table-wrap"><table className="table">
        <tbody>
          <Section title="Sales" rows={d.sections.sales} total={d.totals.revenue} />
          <Section title="Cost of sales" rows={d.sections.costOfSales} total={d.totals.costOfSales} />
          <tr><td><strong>Gross profit</strong></td><td className="num"><strong>{formatGBP(d.totals.grossProfit)}</strong></td></tr>
          <Section title="Other income" rows={d.sections.otherIncome} total={d.totals.otherIncome} />
          <Section title="Wages" rows={d.sections.wages} total={d.totals.wages} />
          <Section title="Overheads" rows={d.sections.overheads} total={d.totals.overheads} />
          <Section title="Depreciation" rows={d.sections.depreciation} total={d.totals.depreciation} />
        </tbody>
        <tfoot><tr><td>Operating profit (before tax)</td><td className="num" style={{ color: d.totals.operatingProfit < 0 ? "var(--red-600)" : undefined }}>{formatGBP(d.totals.operatingProfit)}</td></tr></tfoot>
      </table></div>
      <Alert kind="info" title="Not taxable profit">{d.notTaxableProfit}</Alert>
    </Card>
  )}</Wrap>;
}

function Cash(r: { from: string; to: string }) {
  const q = useReport("cash", r);
  return <Wrap q={q}>{(d) => (
    <div className="stack">
      <Card title="Money accounts" pad={false}>
        <div className="card-body"><Basis text={d.basis} /></div>
        <DataTable rows={d.accounts} rowLink={(a: any) => `/finance/ledger?account=${a.id}`} columns={[
          { key: "n", label: "Account", render: (a: any) => a.name },
          { key: "o", label: "Opening", num: true, render: (a: any) => <Money pence={a.opening} /> },
          { key: "i", label: "In", num: true, render: (a: any) => <Money pence={a.inflow} /> },
          { key: "x", label: "Out", num: true, render: (a: any) => <Money pence={-a.outflow} /> },
          { key: "c", label: "Closing", num: true, render: (a: any) => <strong><Money pence={a.closing} /></strong> },
        ]} />
      </Card>
      <Card title="Where the money came from and went" pad={false}>
        <DataTable rows={d.byType} empty={<Empty title="No movements" />} columns={[
          { key: "l", label: "Type", render: (t: any) => t.label },
          { key: "i", label: "In", num: true, render: (t: any) => <Money pence={t.inflow} /> },
          { key: "o", label: "Out", num: true, render: (t: any) => <Money pence={-t.outflow} /> },
          { key: "n", label: "Net", num: true, render: (t: any) => <Money pence={t.net} /> },
        ]} />
        <div className="card-body small muted">Transfers between your own accounts are left out here because they net to zero.</div>
      </Card>
    </div>
  )}</Wrap>;
}

function Sales(r: { from: string; to: string }) {
  const q = useReport("sales", r);
  return <Wrap q={q}>{(d) => (
    <div className="grid grid-2">
      <Card title="Sales (invoiced)">
        <Basis text={d.basis.sales} />
        <dl className="kv">
          <dt>Invoices issued</dt><dd>{d.sales.invoices.n} · <Money pence={d.sales.invoices.gross} /></dd>
          <dt>Credit notes</dt><dd>{d.sales.creditNotes.n} · −<Money pence={d.sales.creditNotes.gross} /></dd>
          <dt>Net sales (excl. VAT)</dt><dd><strong><Money pence={d.sales.netSales} /></strong></dd>
          <dt>Sales incl. VAT</dt><dd><Money pence={d.sales.grossSales} /></dd>
          {d.sales.byDepartment.map((x: any) => <><dt key={x.department}><DeptBadge d={x.department} /></dt><dd><Money pence={x.net} /></dd></>)}
        </dl>
        <p><Link to={`/invoices?filter=all`}>See invoices</Link></p>
      </Card>
      <Card title="Money received">
        <Basis text={d.basis.received} />
        <dl className="kv">
          {d.received.byMethod.map((m: any) => <><dt key={m.method + m.kind}>{PAYMENT_METHOD_LABEL[m.method as keyof typeof PAYMENT_METHOD_LABEL]}{m.kind === "deposit" ? " (deposits)" : ""}</dt><dd><Money pence={m.total} /> <span className="small muted">({m.n})</span></dd></>)}
          <dt>Refunds paid</dt><dd>−<Money pence={d.received.refunds} /></dd>
          <dt>Net received</dt><dd><strong><Money pence={d.received.netReceived} /></strong></dd>
          <dt>of which deposits</dt><dd><Money pence={d.received.depositsIncluded} /></dd>
        </dl>
        <p><Link to={`/payments${qs({ from: d.from, to: d.to })}`}>See payments</Link></p>
      </Card>
      <div className="full" style={{ gridColumn: "1 / -1" }}>
        <Alert kind="info" title={`Difference: ${formatGBP(d.difference)}`}>Invoiced (incl. VAT) minus money received. Positive = more invoiced than collected (unpaid invoices); negative = deposits or old debts collected in this period.</Alert>
      </div>
    </div>
  )}</Wrap>;
}

function Debtors() {
  const q = useReport("debtors");
  return <Wrap q={q}>{(d) => (
    <Card title={`Unpaid customer invoices — ${formatGBP(d.total)}`} pad={false}>
      <div className="card-body"><Basis text={d.basis} /><div className="row">{["current", "1-30", "31-60", "61-90", "90+"].map((b) => <Badge key={b} tone={b === "90+" || b === "61-90" ? "red" : b === "current" ? undefined : "amber"}>{b === "current" ? "Not yet due" : `${b} days`}: {formatGBP(d.buckets[b] ?? 0)}</Badge>)}</div></div>
      <DataTable rows={d.rows} rowLink={(r: any) => `/invoices/${r.id}`} empty={<Empty title="Nothing owed" />} columns={[
        { key: "n", label: "Invoice", render: (r: any) => r.number },
        { key: "c", label: "Customer", render: (r: any) => <>{r.customer_name}<div className="small muted">{r.phone}</div></> },
        { key: "d", label: "Issued", render: (r: any) => date(r.issue_date) },
        { key: "a", label: "Overdue", render: (r: any) => r.daysOverdue > 0 ? <Badge tone={r.daysOverdue > 60 ? "red" : "amber"}>{r.daysOverdue} days</Badge> : "Not yet due" },
        { key: "o", label: "Outstanding", num: true, render: (r: any) => <Money pence={r.outstanding_pence} /> },
      ]} />
    </Card>
  )}</Wrap>;
}

function Creditors() {
  const q = useReport("creditors");
  return <Wrap q={q}>{(d) => (
    <Card title={`Unpaid supplier bills — ${formatGBP(d.total)}`} pad={false}>
      <div className="card-body"><Basis text={d.basis} />{d.ledgerBalancePence !== d.total && <Alert kind="info">Ledger balance owed to suppliers is {formatGBP(d.ledgerBalancePence)} after supplier returns/credits.</Alert>}</div>
      <DataTable rows={d.rows} rowLink={(r: any) => `/bills/${r.id}`} empty={<Empty title="Nothing owed to suppliers" />} columns={[
        { key: "s", label: "Supplier", render: (r: any) => r.supplier_name },
        { key: "r", label: "Their ref", render: (r: any) => r.supplier_ref },
        { key: "d", label: "Bill date", render: (r: any) => date(r.bill_date) },
        { key: "a", label: "Overdue", render: (r: any) => r.daysOverdue > 0 ? <Badge tone="amber">{r.daysOverdue} days</Badge> : "Not yet due" },
        { key: "o", label: "Outstanding", num: true, render: (r: any) => <Money pence={r.outstanding_pence} /> },
      ]} />
    </Card>
  )}</Wrap>;
}

function Expenses(r: { from: string; to: string }) {
  const q = useReport("expenses", r);
  return <Wrap q={q}>{(d) => (
    <Card title="Costs by category" pad={false}>
      <div className="card-body"><Basis text={d.basis} /></div>
      <DataTable rows={d.rows} rowLink={(x: any) => `/finance/ledger${qs({ account: x.id, from: d.from, to: d.to })}`} empty={<Empty title="No costs in this period" />} columns={[
        { key: "n", label: "Category", render: (x: any) => <>{x.name} <span className="small muted">{x.code}</span></> },
        { key: "e", label: "Entries", num: true, render: (x: any) => x.entries },
        { key: "a", label: "Amount", num: true, render: (x: any) => <Money pence={x.amount} /> },
      ]} footer={d.rows.length ? <tr><td colSpan={2} className="right">Total</td><td className="num">{formatGBP(d.rows.reduce((a: number, x: any) => a + x.amount, 0))}</td></tr> : undefined} />
    </Card>
  )}</Wrap>;
}

function Jobs(r: { from: string; to: string }) {
  const q = useReport("jobs", r);
  return <Wrap q={q}>{(d) => (
    <Card title="Job contribution (estimate)" pad={false}>
      <div className="card-body">
        <Basis text={d.basis} />
        <Alert kind="info">{d.noDoubleCounting}</Alert>
        {d.incompleteCount > 0 && <div style={{ marginTop: 8 }}><Alert kind="warn">{d.incompleteCount} job(s) have incomplete costs — their contribution is overstated.</Alert></div>}
      </div>
      <DataTable rows={d.rows} rowLink={(x: any) => `/jobs/${x.id}`} empty={<Empty title="No invoiced jobs in this period" />} columns={[
        { key: "n", label: "Job", render: (x: any) => <>{x.number} <DeptBadge d={x.department} /><div className="small muted">{x.customer_name}</div></> },
        { key: "r", label: "Revenue", num: true, render: (x: any) => <Money pence={x.revenue} /> },
        { key: "p", label: "Parts", num: true, render: (x: any) => <><Money pence={x.partsFromStock + x.partsEstimated} />{x.partsEstimated > 0 && <div className="small muted">incl. est. {formatGBP(x.partsEstimated)}</div>}</> },
        { key: "l", label: "Labour (allocated)", num: true, render: (x: any) => <><Money pence={x.labourAllocated} /><div className="small muted">{Math.round(x.minutes / 6) / 10}h</div></> },
        { key: "c", label: "Contribution", num: true, render: (x: any) => <strong><Money pence={x.contribution} /></strong> },
        { key: "f", label: "Cost status", render: (x: any) => x.complete ? <Badge tone="green">Complete</Badge> : <>{x.flags.map((f: string) => <div key={f}><Badge tone="amber">{f}</Badge></div>)}</> },
      ]} footer={d.rows.length ? <tr><td>Total</td><td className="num">{formatGBP(d.totals.revenue)}</td><td className="num">{formatGBP(d.totals.parts)}</td><td className="num">{formatGBP(d.totals.labour)}</td><td className="num">{formatGBP(d.totals.contribution)}</td><td /></tr> : undefined} />
    </Card>
  )}</Wrap>;
}

function Departments(r: { from: string; to: string }) {
  const q = useReport("departments", r);
  return <Wrap q={q}>{(d) => (
    <Card title="Mechanical vs bodywork">
      <Basis text={d.basis} />
      <div className="table-wrap"><table className="table">
        <thead><tr><th /><th className="num">Mechanical</th><th className="num">Bodywork</th></tr></thead>
        <tbody>
          <tr><td>Sales</td><td className="num">{formatGBP(d.mechanical.sales)}</td><td className="num">{formatGBP(d.bodywork.sales)}</td></tr>
          <tr><td>Direct costs</td><td className="num">{formatGBP(d.mechanical.directCosts)}</td><td className="num">{formatGBP(d.bodywork.directCosts)}</td></tr>
          <tr><td><strong>Contribution</strong></td><td className="num"><strong>{formatGBP(d.mechanical.contribution)}</strong></td><td className="num"><strong>{formatGBP(d.bodywork.contribution)}</strong></td></tr>
          <tr><td>Jobs collected</td><td className="num">{d.mechanical.jobsCollected}</td><td className="num">{d.bodywork.jobsCollected}</td></tr>
        </tbody>
      </table></div>
      <p>Shared costs not split between departments: <strong>{formatGBP(d.sharedCosts)}</strong> (wages, rent, utilities etc.).</p>
    </Card>
  )}</Wrap>;
}

function TrialBalance({ asOf }: { asOf: string }) {
  const q = useReport("trial-balance", { asOf });
  return <Wrap q={q}>{(d) => (
    <Card title={`Trial balance at ${date(d.asOf)}`} actions={d.balanced ? <Badge tone="green">Balanced</Badge> : <Badge tone="red">NOT balanced</Badge>} pad={false}>
      <DataTable rows={d.rows} rowLink={(x: any) => `/finance/ledger${qs({ account: `acc-${x.code}` })}`} columns={[
        { key: "c", label: "Code", render: (x: any) => x.code },
        { key: "n", label: "Account", render: (x: any) => x.name },
        { key: "d", label: "Debit", num: true, render: (x: any) => (x.balance > 0 ? formatGBP(x.balance) : "") },
        { key: "k", label: "Credit", num: true, render: (x: any) => (x.balance < 0 ? formatGBP(-x.balance) : "") },
      ]} footer={<tr><td colSpan={2}>Totals (all postings)</td><td className="num">{formatGBP(d.totals.debit)}</td><td className="num">{formatGBP(d.totals.credit)}</td></tr>} />
    </Card>
  )}</Wrap>;
}

function Exports(r: { from: string; to: string }) {
  const files = [
    ["ledger.csv", "Full ledger (every posting with source record)"], ["trial-balance.csv", "Trial balance at the end date"], ["invoices.csv", "Sales invoices"], ["credit-notes.csv", "Credit notes"],
    ["payments.csv", "Customer payments & deposits"], ["expenses.csv", "Expenses (with receipt counts)"], ["bills.csv", "Supplier bills"], ["wages.csv", "Attendance & wages earned"],
  ];
  return (
    <Card title="Accountant exports (CSV)">
      <p className="muted">For {date(r.from)} – {date(r.to)}. These are the supporting records for your accountant; they are not filed with HMRC or Companies House by this system.</p>
      <ul>{files.map(([f, label]) => <li key={f} style={{ margin: "8px 0" }}><a href={`/api/reports/export/${f}${qs(r)}`} download>{label}</a> <span className="small muted">({f})</span></li>)}</ul>
    </Card>
  );
}
